/**
 * Municípios para a busca: os nomes vêm do mesmo índice do filtro dos focos
 * (`fires/areaSearch.ts`, `public/data/busca.json`, baixado no primeiro foco
 * no campo) e os limites, da camada `municipios` do módulo de vetores
 * (`fires/areaGeometry.ts` `resolveArea`, baixada na primeira escolha). A
 * localização usa o ponto-no-polígono que já estima o município dos focos
 * (`fires/municipio.ts`).
 */
import { createEmpty, extend, isEmpty, type Extent } from 'ol/extent';
import type Geometry from 'ol/geom/Geometry';
import { resolveArea } from '../fires/areaGeometry';
import { estimateMunicipio } from '../fires/municipio';
import { getLoadedAreaIndex, loadAreaIndex, searchAreas, type AreaEntry, type AreaIndex } from '../fires/areaSearch';
import { useMapInstance } from '../map/mapContext';
import { lonLatToMap } from '../map/projection';
import { enquadrarExtensao } from '../map/viewControl';
import { useBuscaStore, type MunicipioEscolhido } from './store';

/** Os municípios que casam com `texto`, do melhor para o pior. */
export function buscarMunicipios(indice: AreaIndex, texto: string, limite = 8): MunicipioEscolhido[] {
  const soMunicipios: AreaIndex = { entries: indice.entries.filter((e) => e.layer === 'municipios'), byId: indice.byId };
  return searchAreas(soMunicipios, texto, { limit: limite }).map(paraEscolhido);
}

function paraEscolhido(e: AreaEntry): MunicipioEscolhido {
  return { cd: e.key, nome: e.name, uf: e.detail };
}

export { getLoadedAreaIndex, loadAreaIndex };

/** A extensão (projeção do mapa) das geometrias, ou null. */
export function extensaoDe(geometrias: readonly Geometry[]): Extent | null {
  const e = createEmpty();
  for (const g of geometrias) extend(e, g.getExtent());
  return isEmpty(e) ? null : e;
}

/** As geometrias do município (baixa a camada na primeira vez). */
export async function geometriasDo(cd: string): Promise<Geometry[]> {
  const area = await resolveArea({ layer: 'municipios', key: cd });
  return area.geometries;
}

/** Enquadra o município no mapa (quando os limites chegarem). */
export async function enquadrarMunicipio(cd: string): Promise<void> {
  const geometrias = await geometriasDo(cd);
  const map = useMapInstance.getState().map;
  const extensao = extensaoDe(geometrias);
  if (map && extensao && useBuscaStore.getState().municipio?.cd === cd) enquadrarExtensao(map, extensao);
}

/** Escolhe o município: destaca e enquadra. */
export function escolherMunicipio(m: MunicipioEscolhido): void {
  // Escolhido pelo nome, o ponto de uma localização anterior sai (a localização o repõe depois).
  useBuscaStore.getState().setLocal(null, 'parado');
  useBuscaStore.getState().escolher(m);
  void enquadrarMunicipio(m.cd).catch((e: unknown) => console.warn('[AmaView] Busca: limites do município não carregaram:', e));
}

/** Completa o nome de um município que veio só com o código (pelo link). */
export async function completarNome(cd: string): Promise<void> {
  const indice = getLoadedAreaIndex() ?? (await loadAreaIndex());
  const e = indice.byId.get(`municipios:${cd}`);
  const atual = useBuscaStore.getState().municipio;
  if (e && atual?.cd === cd) useBuscaStore.setState({ municipio: paraEscolhido(e) });
}

/** Mensagens da localização, em linguagem de usuário. */
export const MENSAGEM_DO_GPS: Record<string, string> = {
  localizando: 'Procurando a sua localização…',
  negado: 'O navegador não liberou a localização. Dá para liberar nas permissões do site.',
  fora: 'A sua localização não fica num município da Amazônia Legal.',
  erro: 'Não foi possível obter a localização agora.',
};

/**
 * "Usar minha localização": SÓ por toque da pessoa — o navegador pede a
 * permissão nessa hora, nunca ao abrir o site (pedido do usuário). Acha o
 * município que contém o ponto, destaca e enquadra, com um ponto no lugar.
 */
export function localizar(): void {
  const st = useBuscaStore.getState();
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    st.setGps('erro');
    return;
  }
  st.setGps('localizando');
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const ponto = lonLatToMap(pos.coords.longitude, pos.coords.latitude);
      void estimateMunicipio(ponto[0], ponto[1])
        .then((m) => {
          if (!m?.cdMun) {
            useBuscaStore.getState().setLocal(ponto, 'fora');
            return;
          }
          escolherMunicipio({ cd: m.cdMun, nome: m.nome, uf: m.uf ?? '' });
          useBuscaStore.getState().setLocal(ponto, 'ok');
        })
        .catch(() => useBuscaStore.getState().setGps('erro'));
    },
    (err) => useBuscaStore.getState().setGps(err.code === err.PERMISSION_DENIED ? 'negado' : 'erro'),
    { enableHighAccuracy: false, timeout: 15_000, maximumAge: 5 * 60_000 },
  );
}
